/**
 * Cloud save sync.
 *
 * The payload is your GameState.serialize() object, stored as a jsonb row in `saves`
 * keyed by user id (Row-Level Security keeps each row owner-only). localStorage is kept
 * as a per-user offline cache so a dropped connection never loses progress.
 */
import { supabase } from "./supabase";

const CACHE_PREFIX = "space-adventura-save-v1:"; // per-user local cache key

function cacheKey(userId: string): string {
  return CACHE_PREFIX + userId;
}
function readCache(userId: string): unknown {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCache(userId: string, data: object): void {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(data));
  } catch {
    /* storage full / unavailable */
  }
}

/** Load the player's save: cloud first, then local cache (offline). null = brand-new player. */
export async function loadCloudSave(userId: string): Promise<unknown> {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from("saves")
        .select("data")
        .eq("user_id", userId)
        .maybeSingle();
      const row = data as { data?: unknown } | null;
      // A blanked ({}) row is a reset tombstone — treat it as "no save" so a fresh game starts.
      if (!error && row && row.data && Object.keys(row.data as object).length > 0) {
        writeCache(userId, row.data as object);
        return row.data;
      }
    } catch {
      /* fall through to the offline cache */
    }
  }
  return readCache(userId);
}

/** Persist the save: always cache locally, and upsert to the cloud when online. */
export async function saveCloudSave(userId: string, data: object): Promise<void> {
  writeCache(userId, data);
  if (!supabase) return;
  const version = typeof (data as Record<string, unknown>).v === "number"
    ? ((data as Record<string, unknown>).v as number)
    : 1;
  try {
    await supabase
      .from("saves")
      .upsert({ user_id: userId, data, version, updated_at: new Date().toISOString() });
  } catch {
    /* offline — the local cache still holds the latest */
  }
}

/** Wipe the save (reset progress) — cloud row + local cache. */
export async function deleteCloudSave(userId: string): Promise<void> {
  try {
    localStorage.removeItem(cacheKey(userId));
  } catch {
    /* ignore */
  }
  if (!supabase) return;
  try {
    // Blank the row first — the owner UPDATE policy always allows this, so "Reset progress"
    // works even before the saves_delete policy is applied (loadCloudSave treats {} as no save).
    await supabase
      .from("saves")
      .update({ data: {}, version: 0, updated_at: new Date().toISOString() })
      .eq("user_id", userId);
    // Then hard-delete the row where the saves_delete policy exists (clean removal).
    await supabase.from("saves").delete().eq("user_id", userId);
  } catch {
    /* ignore */
  }
}
